import { QuaggaProcessResult } from 'services/barcode-decoder.service';

export type CodeDetectionStatus =
  'confirmed' |
  'conflicting' | // a "correct" code is detected, but it's not the same as the previous one
  'unconfirmed' |
  'partial' |
  'none';

/** Require at least this many _successive_ detections of the same code. */
export const MIN_SUCCESSIVE_SAME = 5;
/** Partial reads closer together than this are counted as one bad read. */
export const BAD_READ_MIN_GAP_SECS = 1;
/** More than this many counted bad reads nudge the user. */
export const BAD_READS_BEFORE_NUDGE = 5;
/** With no code read at all for longer than this, nudge the user. */
export const NO_CODE_NUDGE_AFTER_SECS = 10;

/** What one decoder frame means for the scanner page. The page acts on it;
 *  this rule never touches the camera, settings, toasts or navigation. */
export interface FrameDecision {
  status: CodeDetectionStatus;
  /** the code read on this frame, if any (the confirmed code when status is 'confirmed') */
  code?: string;
  /** true only the first time a "try better lighting / another camera" hint is warranted */
  nudge: boolean;
}

/* Check potential results for false positives and partial detections.

IMPORTANT: Bad lighting conditions, wrong camera selection, etc can cause either a lot of partials without a fully correct detection,
or they can also cause a false detection! That is a different number read, but the checksum is accidentally correct.

A good example of this is "MyProtein "Oatmeal & Raisins XTra Cookie", EAN 5055936815490, which is often read as these "impostors":

  151930815490
  4051946815421
  4051946815490
  4057948815490
  5051938815490
  5055936815490
  5155936814490
  6019784219014
  6055270815490
  9055940815420

Look at the comparison:
https://barcode.tec-it.com/barcode.ashx?data=5055936815490&code=EAN13
https://barcode.tec-it.com/barcode.ashx?data=4051946815490&code=EAN13

So we apply this strategy:
1. We require several detections in a row of the same number.
2. If we find that we are getting "flashes" of partial or incosistent codes, we want to nudge the user to
try to improve their framing, or to switch cameras.

The time comes in as an argument, so this rule has no clock of its own and can
be unit-tested without a camera (barcode-detection-stability.test.ts).
*/
export class BarcodeDetectionStability {
  // this is used to prevent false positives in bad lighting conditions
  private lastCode: string;
  private successiveCorrectCount: number;

  // these are used to detect that this might be the wrong camera, and to nudge the user to switch
  private cameraStartedTime: number = undefined;
  private incorrectDetectionCount: number;
  /** when the last partial read that was COUNTED arrived (ms), or undefined if none since the last reset */
  private lastCountedBadReadTime: number = undefined;
  private badDetectionReported = false; // to prevent multiple warnings until the user switches the camera

  /** When the first frame arrived (ms), or undefined before that. The page's
   *  template reads it to tell whether a stream ever started. */
  get startedTime(): number {
    return this.cameraStartedTime;
  }

  /** True once the first frame has seeded the timing via start(). */
  get started(): boolean {
    return this.cameraStartedTime !== undefined;
  }

  /** Seed the timing on the first frame, so incorrect-detection timing starts
   *  only once we actually have a stream going. */
  start(nowMs: number) {
    this.cameraStartedTime = nowMs;
    this.incorrectDetectionCount = 0;
    this.lastCountedBadReadTime = undefined;
    this.lastCode = undefined;
    this.successiveCorrectCount = 0;
    this.badDetectionReported = false;
  }

  /** Feed one decoder frame through the rule. Updates the run and bad-read
   *  state, and answers what the frame means and whether to show the hint. */
  observeFrame(result: QuaggaProcessResult, nowMs: number): FrameDecision {
    const { status, code } = this.classify(result);
    const warn = this.trackIncorrectCodes(status, nowMs);
    let nudge = false;
    if (warn && !this.badDetectionReported) {
      this.badDetectionReported = true;
      nudge = true;
    }
    return { status, code, nudge };
  }

  /** Classify one frame, and advance the run of successive identical reads
   *  (lastCode / successiveCorrectCount) as a side effect. */
  private classify(result: QuaggaProcessResult): { status: CodeDetectionStatus; code?: string } {
    const code = result && result.codeResult && result.codeResult.code;

    // if there is some code detected at all - check for its stability....
    if (code) {
      console.log('Barcodes: potential code: ', code);

      if (this.lastCode != code) {
        const conflictResult: CodeDetectionStatus = this.lastCode ? 'conflicting' : 'unconfirmed';
        this.lastCode = code;
        this.successiveCorrectCount = 1;
        return { status: conflictResult, code };
      }

      this.successiveCorrectCount++;

      if (this.successiveCorrectCount >= MIN_SUCCESSIVE_SAME) {
        console.log('Barcodes: CONFIRMED code: ', code);
        return { status: 'confirmed', code };
      }

      return { status: 'unconfirmed', code };
    }

    // if there are boxes, but no code
    if (result && result.boxes && result.boxes.length > 0) {
      // it's a partial detection
      return { status: 'partial' };
      // if there are no boxes
    } else {
      // nothing is in the image
      return { status: 'none' };
    }
  }

  /** Returns whether a bad-camera warning is warranted on this frame. */
  private trackIncorrectCodes(status: CodeDetectionStatus, now: number): boolean {
    // confirmed or unconfirmed detections reset the counters
    if (status === 'confirmed' || status === 'unconfirmed') {
      this.incorrectDetectionCount = 0;
      this.lastCountedBadReadTime = undefined;
      return false;
    }

    let shouldWarn = false;
    // in case of conflicting codes, warn immediately
    if (status === 'conflicting') {
      shouldWarn = true;
    }

    if (status === 'partial') {
      // the first partial read since the last reset counts; after that, reads
      // closer together than the gap count as one (a burst while focusing)
      const isFirstBadRead = this.lastCountedBadReadTime === undefined;
      if (isFirstBadRead || (now - this.lastCountedBadReadTime) / 1000 > BAD_READ_MIN_GAP_SECS) {
        this.incorrectDetectionCount++;
        this.lastCountedBadReadTime = now;
      }
    }

    let secondsSinceStart = (now - this.cameraStartedTime) / 1000;

    // if we have a lot of spaced-out bad detections
    if (this.incorrectDetectionCount > BAD_READS_BEFORE_NUDGE) {
      shouldWarn = true;
    }

    // if no code was ever read and the camera is on for a long time (whether or
    // not there were partial reads: whichever of the two routes comes first wins)
    if (this.successiveCorrectCount === 0 && secondsSinceStart > NO_CODE_NUDGE_AFTER_SECS) {
      shouldWarn = true;
    }

    return shouldWarn;
  }
}
